import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { useCreateNativeLibraryItem, useCoachOwnWorkspaces } from "@/lib/coachWorkbenchQueries";

const CONTENT_TYPES = ["exercise", "framework", "template", "guide", "checklist"];
const DIFFICULTIES = ["beginner", "intermediate", "advanced"];
const RENDERER_KEYS = ["content_viewer", "guided_practice", "reflection", "checklist", "interactive_worksheet", "card_board", "structure_builder", "annotated_text", "upload_media"];

/**
 * Master v5 Prompt 20 — authors a LibraryItem's first draft version
 * directly (no File promotion needed). content_ref stays the fixed
 * opaque "native" marker; the real authored content lives in these
 * plain metadata fields, never inside content_ref.
 */
export function LibraryCreateForm({ onClose }) {
  const createItem = useCreateNativeLibraryItem();
  const { data: workspacesData } = useCoachOwnWorkspaces();
  const workspaces = workspacesData?.items || [];

  const [form, setForm] = useState({
    content_type: "exercise", title: "", use_when: "", develops: "", use_case: "", outcome: "",
    duration_minutes: "", difficulty: "", language: "en", audit_eligible: false,
    individual_or_team: ["individual"], scope: "reusable", owner_workspace_id: "",
    focus_keys: "", programme_keys: "", renderer_key: "",
  });
  const [steps, setSteps] = useState([]);

  function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }
  function toggleAudience(v) {
    setForm((f) => ({ ...f, individual_or_team: f.individual_or_team.includes(v) ? f.individual_or_team.filter((x) => x !== v) : [...f.individual_or_team, v] }));
  }
  function addStep() { setSteps((s) => [...s, { order_index: s.length, title: "", body_text: "" }]); }
  function updateStep(i, field, value) { setSteps((s) => s.map((step, idx) => (idx === i ? { ...step, [field]: value } : step))); }
  function removeStep(i) { setSteps((s) => s.filter((_, idx) => idx !== i).map((step, idx) => ({ ...step, order_index: idx }))); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.scope === "client_specific" && !form.owner_workspace_id) return toast.error("Pick a Workspace for a client-specific item");
    try {
      await createItem.mutateAsync({
        ...form,
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
        difficulty: form.difficulty || null,
        owner_workspace_id: form.scope === "client_specific" ? form.owner_workspace_id : null,
        focus_keys: form.focus_keys.split(",").map((s) => s.trim()).filter(Boolean),
        programme_keys: form.programme_keys.split(",").map((s) => s.trim()).filter(Boolean),
        renderer_key: form.renderer_key || null,
        steps: steps.filter((s) => s.title.trim()),
      });
      toast.success("Draft created — publish it from the list when ready");
      onClose();
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't create that item");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border border-terracotta/30 bg-terracotta/5 rounded-lg p-4 space-y-3 max-w-2xl" data-testid="library-create-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-ink">New Library item</h3>
        <button type="button" onClick={onClose} data-testid="library-create-form-close"><X size={14} className="text-ink-soft" /></button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <select value={form.content_type} onChange={(e) => set("content_type", e.target.value)} className="border border-ink/15 rounded-md text-sm px-2 py-1.5 bg-white" data-testid="library-create-content-type">
          {CONTENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)} className="h-8 text-sm" data-testid="library-create-title" />
      </div>

      <Input placeholder="Use when… (shown in Quick Preview)" value={form.use_when} onChange={(e) => set("use_when", e.target.value)} className="h-8 text-sm" data-testid="library-create-use-when" />
      <Input placeholder="Develops…" value={form.develops} onChange={(e) => set("develops", e.target.value)} className="h-8 text-sm" data-testid="library-create-develops" />
      <Textarea placeholder="Use case" value={form.use_case} onChange={(e) => set("use_case", e.target.value)} className="text-sm min-h-16" data-testid="library-create-use-case" />
      <Textarea placeholder="Outcome" value={form.outcome} onChange={(e) => set("outcome", e.target.value)} className="text-sm min-h-16" data-testid="library-create-outcome" />

      <div className="grid grid-cols-3 gap-2">
        <Input type="number" min="1" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="h-8 text-sm" data-testid="library-create-duration" />
        <select value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="border border-ink/15 rounded-md text-sm px-2 py-1.5 bg-white" data-testid="library-create-difficulty">
          <option value="">Difficulty…</option>
          {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <Input placeholder="Language" value={form.language} onChange={(e) => set("language", e.target.value)} className="h-8 text-sm" data-testid="library-create-language" />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="Focus tags (comma-separated)" value={form.focus_keys} onChange={(e) => set("focus_keys", e.target.value)} className="h-8 text-sm" data-testid="library-create-focus-keys" />
        <Input placeholder="Programme tags (comma-separated)" value={form.programme_keys} onChange={(e) => set("programme_keys", e.target.value)} className="h-8 text-sm" data-testid="library-create-programme-keys" />
        <select value={form.renderer_key} onChange={(e) => set("renderer_key", e.target.value)} className="border border-ink/15 rounded-md text-sm px-2 py-1.5 bg-white" data-testid="library-create-renderer-key">
          <option value="">Renderer…</option>
          {RENDERER_KEYS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-4 text-xs text-ink-soft">
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.individual_or_team.includes("individual")} onChange={() => toggleAudience("individual")} /> Individual</label>
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.individual_or_team.includes("team")} onChange={() => toggleAudience("team")} /> Team</label>
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.audit_eligible} onChange={(e) => set("audit_eligible", e.target.checked)} data-testid="library-create-audit-eligible" /> Audit-eligible</label>
      </div>

      <div className="flex items-center gap-2">
        <select value={form.scope} onChange={(e) => set("scope", e.target.value)} className="border border-ink/15 rounded-md text-sm px-2 py-1.5 bg-white" data-testid="library-create-scope">
          <option value="reusable">Reusable (shared catalog)</option>
          <option value="client_specific">Client-specific</option>
        </select>
        {form.scope === "client_specific" && (
          <select value={form.owner_workspace_id} onChange={(e) => set("owner_workspace_id", e.target.value)} className="border border-ink/15 rounded-md text-sm px-2 py-1.5 bg-white flex-1" data-testid="library-create-owner-workspace">
            <option value="">Choose a Workspace…</option>
            {workspaces.map((w) => <option key={w.wrk_id} value={w.wrk_id}>{w.title}</option>)}
          </select>
        )}
      </div>

      <div className="space-y-1.5" data-testid="library-create-steps">
        <div className="flex items-center justify-between">
          <p className="text-xs text-ink-soft">Steps</p>
          <button type="button" onClick={addStep} className="text-xs text-terracotta flex items-center gap-1" data-testid="library-create-add-step"><Plus size={11} /> Add step</button>
        </div>
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input placeholder={`Step ${i + 1} title`} value={step.title} onChange={(e) => updateStep(i, "title", e.target.value)} className="h-7 text-xs flex-1" data-testid={`library-create-step-title-${i}`} />
            <Input placeholder="What to do" value={step.body_text} onChange={(e) => updateStep(i, "body_text", e.target.value)} className="h-7 text-xs flex-[2]" data-testid={`library-create-step-body-${i}`} />
            <button type="button" onClick={() => removeStep(i)} data-testid={`library-create-remove-step-${i}`}><Trash2 size={12} className="text-ink-soft hover:text-red-600" /></button>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid="library-create-cancel">Cancel</Button>
        <Button type="submit" size="sm" disabled={createItem.isPending} data-testid="library-create-submit">Create draft</Button>
      </div>
    </form>
  );
}
